import { createAsyncThunk } from '@reduxjs/toolkit';
import { getOwnRecordsInTimeframe } from '../services/getOwnRecordsInTimeframe.service';
import { myDayPeriodStartMs, startOfNextDayMs } from '../utils/myDay';

/** How long the user's own records are reused before the section asks again. */
export const MY_DAY_RECORDS_MAX_AGE_MS = 5 * 60 * 1000;

/**
 * How far before the week or the month the records are read: the API picks
 * records by their clock-in, and a shift begun the evening before (Sunday
 * night into Monday, the last of the month into the 1st) counts its hours
 * after midnight in the new week, month and day. The figures count only the
 * part of each record inside their own period.
 */
export const MY_DAY_LOOKBACK_MS = 24 * 60 * 60 * 1000;

/**
 * The timeframe "Oma päiväni" reads at `now`: from a day before the start of
 * this week or this month, whichever is earlier, to the end of today, as ISO
 * timestamps.
 * @param {number} now epoch ms
 * @returns {{fromDate: string, toDate: string}}
 */
export const myDayTimeframe = (now) => ({
  fromDate: new Date(
    myDayPeriodStartMs(now) - MY_DAY_LOOKBACK_MS
  ).toISOString(),
  toDate: new Date(startOfNextDayMs(now) - 1).toISOString(),
});

/**
 * The signed-in user's own records for "Oma päiväni" — every project's, over
 * myDayTimeframe — for their day, week and month on the project. Reused for
 * MY_DAY_RECORDS_MAX_AGE_MS within the same timeframe. The page's own records
 * (which the clock buttons keep up to date) are left alone: the section reads
 * both (selectMyDayRecords).
 */
export const loadMyDayRecords = createAsyncThunk(
  'projectOverview/loadMyDayRecords',
  async () => {
    const loadedAt = Date.now();
    const { fromDate, toDate } = myDayTimeframe(loadedAt);
    const records = await getOwnRecordsInTimeframe({ fromDate, toDate });
    return {
      records: Array.isArray(records) ? records : [],
      fromDate,
      loadedAt,
    };
  },
  {
    // One load at a time, and not again while fresh and of the same weeks.
    condition: (arg, { getState }) => {
      const entry = getState().projectOverview?.myDayRecords;
      if (!entry) return true;
      if (entry.status === 'loading') return false;
      if (arg?.force) return true;
      const now = Date.now();
      return !(
        entry.status === 'succeeded' &&
        entry.fromDate === myDayTimeframe(now).fromDate &&
        now - entry.loadedAt < MY_DAY_RECORDS_MAX_AGE_MS
      );
    },
  }
);
